
import React, { useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

const Footer = () => {
  const isMobile = useIsMobile();
  
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;
    
    // Load iubenda.js for privacy/terms modals if not already loaded
    if (!document.querySelector('script[src*="iubenda.js"]')) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = `
        (function (w,d) {
          var loader = function () {
            var s = d.createElement("script"), 
                tag = d.getElementsByTagName("script")[0]; 
            s.src = "https://cdn.iubenda.com/iubenda.js"; 
            tag.parentNode.insertBefore(s,tag);
          }; 
          if(w.addEventListener){
            w.addEventListener("load", loader, false);
          } else if(w.attachEvent){
            w.attachEvent("onload", loader);
          } else {
            w.onload = loader;
          }
        })(window, document);
      `;
      document.head.appendChild(script);
    }
  }, []);
  
  return (
    <footer className="py-12 bg-coderide-black text-white">
      <div className="container px-4 md:px-6">
        <div className={`grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-8 ${isMobile ? 'text-center' : ''}`}>
          <div className={isMobile ? 'flex flex-col items-center' : ''}>
            <div className={`flex items-center gap-2 mb-4 ${isMobile ? 'justify-center' : ''}`}>
              <img 
                src="https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/Logo%20White&Color.svg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL0xvZ28gV2hpdGUmQ29sb3Iuc3ZnIiwiaWF0IjoxNzQ0OTYwNzcxLCJleHAiOjE3NzY0OTY3NzF9.U7Hm-nU3lpRfXBK-5K1QK6jwwqJ76l0RbQ8nZG8Keg4" 
                alt="CodeRide Logo" 
                className="h-5 w-auto"
                loading="lazy"
              />
            </div>
            <div className={`max-w-xs ${isMobile ? 'mx-auto' : ''}`}>
              <p className="text-white/70">Transforming AI-assisted coding experiences for developers worldwide.</p>
            </div>
            {/* Product Hunt badge moved from hero to footer */}
            <div className={`${isMobile ? 'mt-6 flex justify-center' : 'mt-6'}`}>
              <a
                href="https://www.producthunt.com/products/coderide?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-coderide-beta"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="CodeRide featured on Product Hunt"
                className="inline-flex items-center"
              >
                <img
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=961798&theme=dark"
                  alt="CodeRide — Featured on Product Hunt"
                  height="44"
                  style={{ width: "auto", height: "44px" }}
                  className="h-11"
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </div>
          </div>

          <div className={`flex ${isMobile ? 'justify-center' : 'justify-end'} items-center space-x-8`}>
            {/* Privacy Policy Link */}
            <a 
              href="https://www.iubenda.com/privacy-policy/87998612" 
              className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed iubenda-noiframe text-white/70 hover:text-coderide-light-blue transition-colors"
              title="Privacy Policy"
            >
              Privacy
            </a>
            
            {/* Terms and Conditions Link */}
            <a 
              href="https://www.iubenda.com/terms-and-conditions/87998612" 
              className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed iubenda-noiframe text-white/70 hover:text-coderide-light-blue transition-colors"
              title="Terms and Conditions"
            >
              Terms
            </a>
            
            {/* Cookie Policy Link */}
            <a 
              href="https://www.iubenda.com/privacy-policy/87998612/cookie-policy" 
              className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed iubenda-noiframe text-white/70 hover:text-coderide-light-blue transition-colors"
              title="Cookie Policy"
            >
              Cookie Policy
            </a>
          </div>
        </div>

        <div className={`flex flex-col md:flex-row ${isMobile ? 'items-center' : 'justify-between items-center'} mt-12 pt-8 border-t border-white/10`}>
          <p className="text-white/50 text-sm">© 2025 CodeRide. All rights reserved.</p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <a 
              href="https://x.com/coderide_ai" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white/70 hover:text-coderide-light-blue transition-colors"
            >
              <span className="sr-only">X (formerly Twitter)</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 512 512" className="h-5 w-5" fill="currentColor">
                <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 13.8 48H156.4l100.5 132.9L389.2 48zm-24.8 373.8h39.1L151.1 88h-42L364.4 421.8z"/>
              </svg>
            </a>
            <a 
              href="https://www.linkedin.com/showcase/coderide/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white/70 hover:text-coderide-light-blue transition-colors"
            >
              <span className="sr-only">LinkedIn</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 448 512" className="h-5 w-5" fill="currentColor">
                <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"/>
              </svg>
            </a>
            <a 
              href="https://github.com/PixdataOrg/coderide-mcp" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white/70 hover:text-coderide-light-blue transition-colors"
            >
              <span className="sr-only">GitHub</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 496 512" className="h-5 w-5" fill="currentColor">
                <path d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3.3-5.6-1.3-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.3 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5.3-6.2 2.3zm44.2-1.7c-2.9.7-4.9 2.6-4.6 4.9.3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8zM97.2 352.9c-1.3 1-1 3.3.7 5.2 1.6 1.6 3.9 2.3 5.2 1 1.3-1 1-3.3-.7-5.2-1.6-1.6-3.9-2.3-5.2-1zm-10.8-8.1c-.7 1.3.3 2.9 2.3 3.9 1.6 1 3.6.7 4.3-.7.7-1.3-.3-2.9-2.3-3.9-2-.6-3.6-.3-4.3.7zm32.4 35.6c-1.6 1.3-1 4.3 1.3 6.2 2.3 2.3 5.2 2.6 6.5 1 1.3-1.3.7-4.3-1.3-6.2-2.2-2.3-5.2-2.6-6.5-1zm-11.4-14.7c-1.6 1-1.6 3.6 0 5.9 1.6 2.3 4.3 3.3 5.6 2.3 1.6-1.3 1.6-3.9 0-6.2-1.4-2.3-4-3.3-5.6-2z"/>
              </svg>
            </a>
            <a 
              href="mailto:hello@coderide.ai" 
              className="text-white/70 hover:text-coderide-light-blue transition-colors"
            >
              <span className="sr-only">Email</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 512 512" className="h-5 w-5" fill="currentColor">
                <path d="M64 112c-8.8 0-16 7.2-16 16v22.1L220.5 291.7c20.7 17 50.4 17 71.1 0L464 150.1V128c0-8.8-7.2-16-16-16H64zM48 212.2V384c0 8.8 7.2 16 16 16H448c8.8 0 16-7.2 16-16V212.2L322.5 328.1c-38.4 31.5-93.7 31.5-132 0L48 212.2zM0 128C0 92.7 28.7 64 64 64H448c35.3 0 64 28.7 64 64V384c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V128z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
